import { useState } from 'react';
import { theme } from '../theme';

// OnboardingTour — new (Sep 14, per Ali's report: a first-time user
// landing on an empty Dashboard with zero guidance, "throwing a user
// at it" in his own words). Three steps, matching exactly the flow
// Ali described: pin a trip -> we watch -> freeze if needed. Shown
// once, gated by users.has_seen_onboarding (see migrations/
// add_onboarding_tour_flag.sql and routes/trips.py's
// mark_onboarding_seen) rather than localStorage, so reinstalling the
// app or switching devices doesn't bring it back — same reasoning
// already applied to every other piece of real user state in this
// app. Skipping and completing both count as "seen" — there's no
// version of this tour that should reappear once dismissed either way.
const STEPS = [
  {
    emoji: '📍',
    title: 'Pin a trip',
    body: 'Tell us where you want to go, your budget, and your dates. Takes under a minute.',
  },
  {
    emoji: '👀',
    title: "We watch, you wait",
    body: "Checked once a day for 10 days. You'll hear from us the moment a real flight matches your budget — no need to check back yourself.",
  },
  {
    emoji: '❄️',
    title: 'Freeze it if you need time',
    body: "Found a deal but need to ask someone first — a partner, a boss, a group chat? Lock the price for a small fee, refunded automatically if it turns out you didn't need it.",
  },
];

export default function OnboardingTour({ onDone }) {
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px'
      }}
    >
      <div
        style={{
          background: theme.surface, border: `2px solid ${theme.border}`, borderRadius: '0px',
          maxWidth: '380px', width: '100%', padding: '32px 26px', textAlign: 'center'
        }}
      >
        <div style={{ fontSize: '40px', marginBottom: '14px' }}>{current.emoji}</div>
        <div style={{ fontFamily: theme.fontDisplay, fontWeight: 800, fontSize: '19px', color: theme.textPrimary, marginBottom: '10px' }}>
          {current.title}
        </div>
        <div style={{ fontSize: '13.5px', color: theme.textMuted, lineHeight: 1.6, marginBottom: '24px' }}>
          {current.body}
        </div>

        {/* Progress dots — a plain step count ("2 of 3") would work
            just as well functionally, but dots read faster at a
            glance for something this short, and match the same
            "small, quiet, unmissable" visual language as the streak
            tracker's own dot row on the Dashboard. */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '22px' }}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                width: '7px', height: '7px', borderRadius: '50%',
                background: i === step ? theme.structural : theme.border
              }}
            />
          ))}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {!isLast && (
            <button
              onClick={onDone}
              style={{
                flex: 1, background: '#fff', color: theme.textMuted, border: `2px solid ${theme.border}`,
                fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: '13.5px',
                padding: '12px', borderRadius: '0px', cursor: 'pointer'
              }}
            >
              Skip
            </button>
          )}
          <button
            onClick={() => (isLast ? onDone() : setStep(step + 1))}
            style={{
              flex: 1, background: theme.action, color: '#1a1a1a', border: 'none',
              fontFamily: theme.fontDisplay, fontWeight: 800, fontSize: '13.5px',
              padding: '12px', borderRadius: '0px', cursor: 'pointer'
            }}
          >
            {isLast ? "Let's go" : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}